import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setAge,
  setApproxReturn,
  setTotalInvestment,
  setSwpStartYear,
  setYearlyIncrementInWithdrawSwp,
  setYearlyWithdraw,
} from "../redux/calculations/lumpsumSlice";
import Input from "../components/Input";
import { Button } from "../components/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import InfoIcon from "../assets/images/Info-icon.svg";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import { useLocation } from "react-router-dom";
import useLocale from "../../src/locales/index";
import i18n from "../i18n";
import { useTranslation } from "react-i18next";
import DecimalUtils from '../utiles/decimalFormate';

const LumpsumCalc = ({ setShowTable, generateData, scrollToResult }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const { textData } = useLocale();
  const lumpsumCalc = textData.lumpsumCalc;
  const [existingData, setExistingData] = useState(
    JSON.parse(localStorage.getItem(textData.storeData)) || []
  );
  const cAccept = localStorage.getItem("grow-cookies");
  const cookieAccept = JSON.parse(cAccept);

  const {
    age,
    monthlyInvestment,
    approxReturn,
    swpStartYear,
    yearlyWithdraw,
    yearlyIncrementInWithdrawSwp,
  } = useSelector((state) => state.lum);

  const [formattedInvestmentAmount, setFormattedInvestmentAmount] =
    useState("");
  const [formattedYearlyWithdraw, setFormattedYearlyWithdraw] = useState("");

  const sliderTrackRef = useRef(null);
  const ageSliderRef = useRef(null);
  const approxReturnSliderRef = useRef(null);
  const swpStartYearSliderRef = useRef(null);
  const yearlyWithdrawSliderRef = useRef(null);
  const yearlyIncrementInWithdrawSwpSliderRef = useRef(null);

  const formik = useFormik({
    initialValues: {
      age: age,
      totalInvestment: monthlyInvestment,
      approxReturn: approxReturn,
      swpStartYear: swpStartYear,
      yearlyWithdraw: yearlyWithdraw,
      yearlyIncrementInWithdrawSwp: yearlyIncrementInWithdrawSwp,
    },
    validationSchema: Yup.object({
      age: Yup.number()
        .typeError(lumpsumCalc.ageError)
        .required(lumpsumCalc.ageRequired)
        .min(1, lumpsumCalc.ageMin)
        .max(99, lumpsumCalc.ageMax),
      totalInvestment: Yup.number()
        .transform((value, originalValue) => {
          // Remove commas and convert empty string to undefined
          if (typeof originalValue === "string") {
            const unformatted = originalValue.replace(/,/g, "");
            return unformatted.trim() === "" ? undefined : Number(unformatted);
          }
          return value;
        })
        .typeError(lumpsumCalc.totalInvestmentError)
        .required(lumpsumCalc.totalInvestmentRequired)
        .min(1000, lumpsumCalc.totalInvestmentMin)
        .max(20000000, lumpsumCalc.totalInvestmentMax),


      approxReturn: Yup.number()
        .required(lumpsumCalc.approxReturnRequired)
        .typeError(lumpsumCalc.approxReturnError)
        .min(1, lumpsumCalc.approxReturnMin)
        .max(20, lumpsumCalc.approxReturnMax),
      swpStartYear: Yup.number()
        .default(0)
        .typeError(lumpsumCalc.swpStartYearError)
        .min(0, lumpsumCalc.swpStartYearMin)
        .max(45, lumpsumCalc.swpStartYearMax),
      yearlyWithdraw: Yup.number()
        .transform((value, originalValue) => {
          // Remove commas and convert empty string to undefined
          if (typeof originalValue === "string") {
            const unformatted = originalValue.replace(/,/g, "");
            return unformatted.trim() === "" ? undefined : Number(unformatted);
          }
          return value;
        })
        .typeError(lumpsumCalc.yearlyWithdrawError)
        // .default(0)
        .min(0, lumpsumCalc.yearlyWithdrawMin)
        .max(1000000, lumpsumCalc.yearlyWithdrawMax),
      yearlyIncrementInWithdrawSwp: Yup.number()
        .required(lumpsumCalc.yearlyIncrementInWithdrawSwpError)
        .typeError(lumpsumCalc.yearlyIncrementInWithdrawSwpError)
        .default(0)
        .min(0, lumpsumCalc.yearlyIncrementInWithdrawSwpMin)
        .max(100, lumpsumCalc.yearlyIncrementInWithdrawSwpMax),
    }),
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: async (values) => {
      if (cookieAccept && analytics) {
        logEvent(analytics, "calculate_LUMPSUM");
      }
      // cookieAccept && logEvent(analytics,`calculate_LUMPSUM`)
      const data = await generateData();
      const isDuplicate = existingData?.some((item) => {
        return JSON.stringify(item) === JSON.stringify(data);
      });
      if (!isDuplicate) {
        const updatedData = [...existingData, data];
        if (updatedData.length > 20) {
          updatedData.shift();
        }
        setExistingData(updatedData);
        localStorage.setItem(textData.storeData, JSON.stringify(updatedData));
      }

      setShowTable(false);
      scrollToResult();
    },
  });

  const handleReturnRateChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("approxReturn", rawValue);
      dispatch(setApproxReturn(""));
      return;
    }
    if (rawValue.length > 5) return;
    if (!DecimalUtils.getValidationPattern().test(rawValue)) return;
    const normalizedValue = DecimalUtils.normalizeInput(rawValue);

    formik.setFieldValue("approxReturn", normalizedValue);
    dispatch(setApproxReturn(normalizedValue));

  };


  const handleWithdrawSwpChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("yearlyIncrementInWithdrawSwp", rawValue);
      dispatch(setYearlyIncrementInWithdrawSwp(""));
      return;
    }
    if (rawValue.length > 5) return;
    if (!DecimalUtils.getValidationPattern().test(rawValue)) return;
    const normalizedValue = DecimalUtils.normalizeInput(rawValue);

    formik.setFieldValue("yearlyIncrementInWithdrawSwp", normalizedValue);
    dispatch(setYearlyIncrementInWithdrawSwp(normalizedValue));

  };


  useEffect(() => {
    setFormattedInvestmentAmount(formatToINR(formik.values.totalInvestment));
    setFormattedYearlyWithdraw(formatToINR(formik.values.yearlyWithdraw));
  }, [formik.values.totalInvestment, formik.values.yearlyWithdraw]);
  const isRTL = () => {
    const rtlLanguages = ["ar", "fa", "ur"];
    return rtlLanguages.includes(i18n.language);
  };
  const handleAgeSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setAge(value));
    formik.setFieldValue("age", value);
  };

  const handleInvestmentChange = (e) => {
    const filtered = e.target.value.replace(/[^0-9,]/g, "");
    const rawValue = filtered.replace(/,/g, "");
    const numValue = rawValue === "" ? "" : parseFloat(rawValue);

    formik.setFieldValue("totalInvestment", numValue);
    dispatch(setTotalInvestment(numValue));
    setFormattedInvestmentAmount(filtered);
  };

  const handleInvestmentSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setTotalInvestment(value));
    formik.setFieldValue("totalInvestment", value);
    setFormattedInvestmentAmount(formatToINR(value.toString()));
  };

  const handleReturnSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setApproxReturn(value));
    formik.setFieldValue("approxReturn", value);
  };

  const handleSwpStartYearSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setSwpStartYear(value));
    formik.setFieldValue("swpStartYear", value);
  };

  const handleYearlyWithdrawChange = (e) => {
    const filtered = e.target.value.replace(/[^0-9,]/g, "");

    const rawValue = filtered.replace(/,/g, "");
    const numValue = rawValue === "" ? "" : parseFloat(rawValue);

    formik.setFieldValue("yearlyWithdraw", numValue);
    dispatch(setYearlyWithdraw(numValue));
    setFormattedYearlyWithdraw(formatToINR(rawValue));
  };

  const handleYearlyWithdrawSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setYearlyWithdraw(value));
    formik.setFieldValue("yearlyWithdraw", value);
    setFormattedYearlyWithdraw(formatToINR(value.toString()));
  };

  const handleYearlyIncrementSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setYearlyIncrementInWithdrawSwp(value));
    formik.setFieldValue("yearlyIncrementInWithdrawSwp", value);
  };

  const formatToINR = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    if (value === 0) return "0";

    const shouldForceIndianFormat = i18n.language === "fa";

    return isNaN(Number(value))
      ? ""
      : Number(value).toLocaleString(
        shouldForceIndianFormat ? "en-IN" : i18n.language || "en-IN"
      );
  };


  const getFormattedPlaceholder = (language) => {
    const baseValue = 10000;
    const formatLang = language === "fa" ? "en-IN" : language;
    return new Intl.NumberFormat(formatLang).format(baseValue);
  };

  const timeLabel = t("yearShort");

  useEffect(() => {
    dispatch(setAge(formik.values.age));
    dispatch(setTotalInvestment(formik.values.totalInvestment));
    dispatch(setApproxReturn(formik.values.approxReturn));
    dispatch(setSwpStartYear(formik.values.swpStartYear));
    dispatch(setYearlyWithdraw(formik.values.yearlyWithdraw));
    dispatch(
      setYearlyIncrementInWithdrawSwp(
        formik.values.yearlyIncrementInWithdrawSwp
      )
    );
  }, [formik.values, dispatch]);

  // Reset form when location changes
  useEffect(() => {
    formik.resetForm();
  }, [location.pathname]);

  return (
    <>
      <div className="">
        <form onSubmit={formik.handleSubmit} noValidate className="flex flex-col gap-6">
          <div>
            <div className="flex justify-between items-center mb-2 gap-4">
              <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                {textData.lumpsumCalc.age}
              </label>
            </div>
            <div className="flex justify-between gap-5 items-center">
              <div className="relative  w-full" ref={ageSliderRef}>

                <input
                  type="range"
                  min={1}
                  max={99}
                  step={1}
                  value={formik.values.age}
                  onChange={handleAgeSliderChange}
                  className="flex-1 min-w-0 max-w-[314px]"
                  style={{
                    width: "100%",
                    appearance: "none",
                    background: "transparent",
                    ...(isRTL() && {
                      direction: "ltr",
                    }),
                    "--val": `${((formik.values.age - 1) / (99 - 1)) * 100}%`,
                  }}
                />
              </div>
              <div className="flex items-center flex-col">
                <Input
                  name="age"
                  leftIcon={timeLabel}
                  value={formik.values.age}
                  onChange={(e) => {
                    if (/^\d{0,2}$/.test(e.target.value)) {
                      formik.handleChange(e);
                      dispatch(setAge(e.target.value));
                    }
                  }}
                  onBlur={formik.handleBlur}
                  type="text"
                  placeholder={"26"}
                  borderColor={
                    formik.touched.age && formik.errors.age
                      ? "border-red-500"
                      : "border-gray-light"
                  }
                  className={` sm:h-[36px] h-[32px]    border ${
                    formik.touched.age && formik.errors.age
                      ? "border-danger"
                      : "border-gray-light"
                  }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                />
                {formik.touched.age && formik.errors.age && (
                  <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                    {formik.errors.age}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-2 gap-4">
              <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                {textData.lumpsumCalc.totalInvestment}
              </label>
            </div>
            <div className="flex justify-between gap-5 items-center">
              <div className="relative  w-full" ref={sliderTrackRef}>

                <input
                  type="range"
                  min={1000}
                  max={20000000}
                  step={1000}
                  value={formik.values.totalInvestment}
                  onChange={handleInvestmentSliderChange} 
                  className="flex-1 min-w-0 max-w-[314px]"
                  style={{
                    width: "100%",
                    appearance: "none",
                    background: "transparent",
                    ...(isRTL() && {
                      direction: "ltr",
                    }),
                    "--val": `${((formik.values.totalInvestment - 1000) /
                      (20000000 - 1000)) *
                      100
                      }%`,
                  }}
                />
              </div>
              <div className="flex items-center flex-col">
                <Input
                  name="totalInvestment"
                  leftIcon={textData.currency}
                  value={formattedInvestmentAmount}
                  onChange={handleInvestmentChange}
                  onBlur={formik.handleBlur}
                  type="text"
                  dir="ltr"
                  placeholder={getFormattedPlaceholder(1000000, i18n.language)}
                  borderColor={
                    formik.touched.totalInvestment &&
                      formik.errors.totalInvestment
                      ? "border-red-500"
                      : "border-gray-light"
                  }
                  className={` sm:h-[36px] h-[32px]    border ${
                    formik.touched.totalInvestment &&
                    formik.errors.totalInvestment
                      ? "border-danger"
                      : "border-gray-light"
                  }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                />
                {formik.touched.totalInvestment &&
                  formik.errors.totalInvestment && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.totalInvestment}
                    </div>
                  )}
              </div>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-2 gap-4">
              <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                {textData.lumpsumCalc.approxReturn}
              </label>
            </div>

            <div className="flex justify-between gap-5 items-center">
              <div className="relative  w-full" ref={approxReturnSliderRef}>

                <input
                  type="range"
                  min={1}
                  max={20}
                  step={0.1}
                  value={parseFloat(formik.values.approxReturn)}
                  onChange={handleReturnSliderChange}
                  className="flex-1 min-w-0 max-w-[314px]"
                  style={{
                    width: "100%",
                    appearance: "none",
                    background: "transparent",
                    ...(isRTL() && {
                      direction: "ltr",
                    }),
                    "--val": `${((formik.values.approxReturn - 1) / (20 - 1)) * 100
                      }%`,
                  }}
                />
              </div>
              <div className="flex items-center flex-col">
                <Input
                  name="approxReturn"
                  leftIcon="%"
                  type="text"
                  placeholder={DecimalUtils.getPlaceholder("6.5")}
                  value={DecimalUtils.formatDisplay(formik.values.approxReturn)}
                  onChange={handleReturnRateChange}
                  onBlur={formik.handleBlur}
                  borderColor={
                    formik.touched.approxReturn && formik.errors.approxReturn
                      ? "border-red-500"
                      : "border-gray-light"
                  }
                  className={` sm:h-[36px] h-[32px]    border ${
                    formik.touched.approxReturn && formik.errors.approxReturn
                      ? "border-danger"
                      : "border-gray-light"
                  }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                />
                {formik.touched.approxReturn && formik.errors.approxReturn && (
                  <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                    {formik.errors.approxReturn}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="border-b border-gray-light mt-[20px] mb-[20px]"></div>

          <div>
            <div className="flex justify-between items-center mb-2 gap-4">
              <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm flex items-center">
                {textData.lumpsumCalc.swpStartYear}
              </label>
            </div>

            <div className="flex justify-between gap-5 items-center">
              <div className="relative  w-full " ref={swpStartYearSliderRef}>

                <input
                  type="range"
                  min={0}
                  max={45}
                  step={1}
                  value={formik.values.swpStartYear}
                  onChange={handleSwpStartYearSliderChange}
                  className="flex-1 min-w-0 max-w-[314px]"
                  style={{
                    width: "100%",
                    appearance: "none",
                    background: "transparent",
                    ...(isRTL() && {
                      direction: "ltr",
                    }),
                    "--val": `${((formik.values.swpStartYear - 0) / (45 - 0)) * 100
                      }%`,
                  }}
                />
              </div>
              <div className="flex items-center flex-col">
                <Input
                  name="swpStartYear"
                  leftIcon={timeLabel}
                  value={formik.values.swpStartYear}
                  onChange={(e) => {
                    formik.handleChange(e);
                    dispatch(setSwpStartYear(e.target.value));
                  }}
                  onBlur={formik.handleBlur}
                  type="number"
                  placeholder={"10"}
                  borderColor={
                    formik.touched.swpStartYear && formik.errors.swpStartYear
                      ? "border-red-500"
                      : "border-gray-light"
                  }
                  className={` sm:h-[36px] h-[32px]    border ${
                    formik.touched.swpStartYear && formik.errors.swpStartYear
                      ? "border-danger"
                      : "border-gray-light"
                  }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                />
                {formik.touched.swpStartYear && formik.errors.swpStartYear && (
                  <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                    {formik.errors.swpStartYear}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-2 gap-4">
              <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm flex items-center gap-2">
                {textData.lumpsumCalc.yearlyWithdraw}

                <div className="relative  w-full group">
                  <img
                    src={InfoIcon}
                    alt="info"
                    className="w-[22px] h-[22px] cursor-pointer"
                  />
                  <div className="absolute hidden -left-[112px] z-50  font-lato-regular-400 group-hover:flex justify-center leading-5 -top-[9px] -translate-y-full sm:w-[350px] w-[280px]  m-auto px-3 py-3 bg-gray rounded-lg text-center text-white text-[12px] after:content-[''] after:absolute  after:top-[100%] sm:after:-translate-x-[67px] after:-translate-x-[32px]  after:border-8 after:border-x-[transparent] after:border-b-[transparent] after:border-t-gray">
                    {textData.lumpsumCalc.info}
                  </div>
                </div>
              </label>
            </div>
            <div className="flex justify-between gap-5 items-center">
              <div className="relative  w-full " ref={yearlyWithdrawSliderRef}>

                <input
                  type="range"
                  min={0}
                  max={1000000}
                  // step={1000}
                  value={formik.values.yearlyWithdraw}
                  onChange={handleYearlyWithdrawSliderChange}
                  className="flex-1 min-w-0 max-w-[314px]"
                  style={{
                    width: "100%",
                    appearance: "none",
                    background: "transparent",
                    ...(isRTL() && {
                      direction: "ltr",
                    }),
                    "--val": `${((formik.values.yearlyWithdraw - 0) / (1000000 - 0)) *
                      100
                      }%`,
                  }}
                />
              </div>
              <div className="flex items-center flex-col">
                <Input
                  name="yearlyWithdraw"
                  leftIcon={textData.currency}
                  value={formattedYearlyWithdraw}
                  onChange={handleYearlyWithdrawChange}
                  onBlur={formik.handleBlur}
                  type="text"
                  dir="ltr"
                  placeholder={getFormattedPlaceholder(50000, i18n.language)} borderColor={
                    formik.touched.yearlyWithdraw &&
                      formik.errors.yearlyWithdraw
                      ? "border-red-500"
                      : "border-gray-light"
                  }
                  className={` sm:h-[36px] h-[32px]    border ${
                    formik.touched.yearlyWithdraw &&
                    formik.errors.yearlyWithdraw
                      ? "border-danger"
                      : "border-gray-light"
                  }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                />
                {formik.touched.yearlyWithdraw &&
                  formik.errors.yearlyWithdraw && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.yearlyWithdraw}
                    </div>
                  )}
              </div>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-2 gap-4">
              <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                {textData.lumpsumCalc.yearlyIncrementInWithdrawSwp}
              </label>
            </div>
            <div className="flex justify-between gap-5 items-center">
              <div
                className="relative  w-full "
                ref={yearlyIncrementInWithdrawSwpSliderRef}
              >

                <input
                  type="range"
                  min={0}
                  max={100}
                  step={0.1}
                  value={parseFloat(formik.values.yearlyIncrementInWithdrawSwp)}
                  onChange={handleYearlyIncrementSliderChange}
                  className="flex-1 min-w-0 max-w-[314px]"
                  style={{
                    width: "100%",
                    appearance: "none",
                    background: "transparent",
                    ...(isRTL() && {
                      direction: "ltr",
                    }),
                    "--val": `${((formik.values.yearlyIncrementInWithdrawSwp - 0) /
                      (100 - 0)) *
                      100
                      }%`,
                  }}
                />
              </div>
              <div className="flex items-center flex-col">
                <Input
                  name="yearlyIncrementInWithdrawSwp"
                  leftIcon="%"
                  type="text"
                  placeholder={DecimalUtils.getPlaceholder("6.5")}
                  value={DecimalUtils.formatDisplay(formik.values.yearlyIncrementInWithdrawSwp)}
                  onChange={handleWithdrawSwpChange}
                  onBlur={formik.handleBlur}
                  borderColor={
                    formik.touched.yearlyIncrementInWithdrawSwp &&
                      formik.errors.yearlyIncrementInWithdrawSwp
                      ? "border-red-500"
                      : "border-gray-light"
                  }
                  className={` sm:h-[36px] h-[32px]    border ${
                    formik.touched.yearlyIncrementInWithdrawSwp &&
                    formik.errors.yearlyIncrementInWithdrawSwp
                      ? "border-danger"
                      : "border-gray-light"
                  }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                />
                {formik.touched.yearlyIncrementInWithdrawSwp &&
                  formik.errors.yearlyIncrementInWithdrawSwp && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.yearlyIncrementInWithdrawSwp}
                    </div>
                  )}
              </div>
            </div>
          </div>

          <div className="mt-[10px]">
            <Button type="submit" className="w-full">
              {textData.calculate}
            </Button>
          </div>
        </form>
      </div>
      {/* </CalculatorLayout> */}
    </>
  );
};

export default LumpsumCalc;
